import * as React from "react";
import styled, { useTheme } from "styled-components";
import { useMemo, useEffect, useCallback, useRef } from "react";
import GenericPlot from "views/components/visualizations/GenericPlot";
import { defaultCompare } from "logic/DefaultCompare";
const CHART_HEIGHT = 300;
const Container = styled.div`
  flex: 1;
  min-width: 400px;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const ChartWrapper = styled.div`
  height: ${CHART_HEIGHT}px;

  g.angularaxistick text {
    pointer-events: all;
    cursor: pointer;
  }
`;
const useChartData = (tactics) => {
  const theme = useTheme();
  return useMemo(() => {
    const tacticsSortedByName = [...tactics].sort(({ name: name1 }, { name: name2 }) => defaultCompare(name1, name2));
    return tacticsSortedByName.length > 0 ? [
      {
        type: "scatterpolar",
        r: [...tacticsSortedByName.map(({ coverage }) => coverage), tacticsSortedByName[0].coverage],
        theta: [...tacticsSortedByName.map(({ name }) => name), tacticsSortedByName[0].name],
        marker: {
          color: theme.colors.charts.cyan.markerColor
        },
        line: {
          shape: "spline"
        },
        selected: {
          marker: {
            size: 0
          }
        },
        hovertemplate: "%{theta}: %{r}%",
        hoverlabel: {
          font: {
            color: theme.colors.charts.cyan.textColor
          }
        },
        fill: "toself",
        fillcolor: theme.colors.charts.cyan.fill,
        name: ""
      }
    ] : [];
  }, [
    tactics,
    theme.colors.charts.cyan.fill,
    theme.colors.charts.cyan.markerColor,
    theme.colors.charts.cyan.textColor
  ]);
};
const useLayout = () => {
  const theme = useTheme();
  return useMemo(
    () => ({
      dragmode: false,
      polar: {
        bgcolor: "transparent",
        radialaxis: {
          visible: true,
          angle: 90,
          range: [0, 105],
          rotation: 90,
          tickfont: {
            color: theme.colors.text.secondary
          },
          gridcolor: theme.colors.charts.lines,
          gridwidth: 2
        },
        angularaxis: {
          direction: "clockwise",
          rotation: 90,
          linewidth: 2,
          linecolor: "transparent",
          tickfont: {
            color: theme.colors.text.primary
          }
        },
        fixedrange: true
      },
      scrollZoom: false,
      margin: {
        b: 40,
        t: 40
      },
      showlegend: false,
      height: CHART_HEIGHT,
      font: {
        color: theme.colors.text.primary
      }
    }),
    [theme.colors.charts.lines, theme.colors.text.primary, theme.colors.text.secondary]
  );
};
const TacticsChart = ({ tactics, onSelectTactic = () => {
} }) => {
  const tacticLabels = useRef();
  const tacticLabelClickEvent = useRef();
  const chartData = useChartData(tactics);
  const layout = useLayout();
  const onAfterPlot = useCallback(() => {
    const axisTicks = document.querySelectorAll("g.angularaxistick");
    tacticLabels.current = axisTicks;
    const _onSelectTactic = (e) => {
      var _a, _b;
      const tacticName = (_b = (_a = e.target) == null ? void 0 : _a.dataset) == null ? void 0 : _b.unformatted;
      const tactic = tactics.find(({ name }) => name === tacticName);
      if (tactic) {
        onSelectTactic(tactic.id);
      }
    };
    for (const trigger of axisTicks) {
      trigger.addEventListener("click", _onSelectTactic);
    }
    tacticLabelClickEvent.current = _onSelectTactic;
  }, [onSelectTactic, tactics]);
  useEffect(
    () => () => {
      var _a;
      if (((_a = tacticLabels.current) == null ? void 0 : _a.length) > 0) {
        tacticLabels.current.forEach((element) => {
          element.removeEventListener("click", tacticLabelClickEvent.current);
        });
      }
    },
    []
  );
  return /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement("h3", null, "Posture"), /* @__PURE__ */ React.createElement(ChartWrapper, null, /* @__PURE__ */ React.createElement(GenericPlot, { chartData, onAfterPlot, layout })));
};
export default TacticsChart;
